import * as qs from 'qs';
import * as actionTypes from '../consts/actionTypes';
import * as Sentry from '@sentry/react';
import { defaultSearchParams } from '../consts/defaultSearchParams';
import { filterParamsSchema } from '../consts/filterParamsSchema';
import { deepClone } from '../helpers/deepClone';
import { getSortingBasedOnMarket, Markets } from '../helpers/getSortingBasedOnMarket';
import { get, post } from '../helpers/http';
import { getQueryParams, IsomorphicHelpers } from '../helpers/IsomorphicHelpers';
import { makeFilterParamsList, makeFilterParamsObject } from '../helpers/queryParams';
import { ICountryCode } from '../interfaces';
import { ICruiseFilterParamValue, ICruiseSearchApiObject } from '../interfaces/ISearchApi';
import { IFeatureFlag } from '../interfaces/IFeatureFlag';

const isDesktop = () => !IsomorphicHelpers.isServerSide && window.innerWidth >= 1200;

export const setFilterParam = (
    filterParams: ICruiseFilterParamValue[],
    doSearch: boolean = false,
) => (dispatch, getState) => {
    filterParams.forEach((filterParam) => {
        dispatch({ type: actionTypes.setFilterParam, payload: filterParam });
    });

    dispatch({ type: actionTypes.searchLoadingToggle, payload: true });
    dispatch(updateFacets())
        .then(() => {
            if (doSearch) {
                doNewSearchViaUrl([])(dispatch, getState);
            }
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
        })
        .catch((_) => {
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
        });
};

export const deleteFilterParam = (filterParam: ICruiseFilterParamValue) => (dispatch, getState) => {
    dispatch({ type: actionTypes.deleteFilterParam, payload: filterParam });
};

export const clearFilterParams = (fetchFacets = true, doSearch = false) => (dispatch, getState) => {
    dispatch({ type: actionTypes.clearFilterParams, payload: null });
    if (fetchFacets) {
        dispatch({ type: actionTypes.searchLoadingToggle, payload: true });
        dispatch(fetchAllFacets())
            .then(() => {
                if (doSearch) {
                    doNewSearchViaUrl([])(dispatch, getState);
                }

                dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
            })
            .catch((_) => {
                dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
            });
    }
};

export const updateFacets = () => (dispatch, getState): Promise<any> => {
    const facetsUrl = `${getState().pageData.appConfig.apiHost}/cruises/facets`;
    const facetParams = getState().search.searchParams;
    delete facetParams.limit; // This would be set as a default from product api
    facetParams.filterParams = makeFilterParamsObject(
        getState().search.filterParamsList,
        filterParamsSchema,
    );
    return post(facetsUrl, facetParams)
        .then((facets) => {
            dispatch({ type: actionTypes.updateFacets, payload: facets });
        })
        .catch((err) => {
            if (getState().router.isBrowser) {
                Sentry.captureException(err, { extra: { ...facetParams, facetsUrl } });
            }
        });
};

export const fetchAllFacets = () => (dispatch, getState): Promise<any> => {
    const facetsUrl = `${getState().pageData.appConfig.apiHost}/cruises/facets`;
    return post(facetsUrl, {})
        .then((facets) => {
            dispatch({ type: actionTypes.updateFacetsAll, payload: facets });
        })
        .catch((err) => {
            if (getState().router.isBrowser) {
                Sentry.captureException(err, { extra: { facetsUrl } });
            }
        });
};

export const fetchAllSearchBoxFacets = () => (dispatch, getState): Promise<any> => {
    const facetsUrl = `${getState().pageData.appConfig.apiHost}/cruises/allfacetssearchbox`;
    return get(facetsUrl)
        .then((facets) => {
            dispatch({ type: actionTypes.updateFacets, payload: facets });
        })
        .catch((err) => {
            if (getState().router.isBrowser) {
                Sentry.captureException(err, { extra: { facetsUrl } });
            }
        });
};

export const setSortOrder = (sortOrder: string) => ({
    type: actionTypes.setSortOrder,
    payload: sortOrder,
});

export const newSearch = (queryString: string) => (dispatch, getState) => {
    const state = getState();
    const searchUrl = `${state.pageData.appConfig.apiHost}/cruises/search-with-aggregation`;
    const isDesktopDevice = state.pageData.deviceType === 'desktop';
    const facetsUrl = `${state.pageData.appConfig.apiHost}/cruises/facets`;

    const searchParamsObject: ICruiseSearchApiObject = queryString
        ? qs.parse(queryString.replace('?', ''), { arrayLimit: 100 })
        : {};

    if (searchParamsObject?.filterParams?.inclFlight) {
        const { inclFlight } = searchParamsObject.filterParams;
        if (typeof inclFlight === 'string') {
            searchParamsObject.filterParams.inclFlight = isNaN(inclFlight)
                ? String(inclFlight).toLowerCase() === 'true'
                : Number(inclFlight) > 0;
        }
    }

    const offset =
        searchParamsObject.offset && isDesktopDevice
            ? parseInt((searchParamsObject.offset as any) as string, 10)
            : 0;
    const limit =
        searchParamsObject.limit && isDesktopDevice
            ? parseInt((searchParamsObject.limit as any) as string, 10)
            : 20;
    const priceMin =
        searchParamsObject?.filterParams?.priceMin || state?.pageData?.brandConfig?.priceMin || 0;

    dispatch({ type: actionTypes.searchLoadingToggle, payload: true });

    const { countryCode } = state?.pageData?.appConfig || (Markets.de as ICountryCode);
    const newSearchParams: ICruiseSearchApiObject = {
        ...defaultSearchParams,
        filterParams: {
            ...searchParamsObject.filterParams,
            priceMin,
        },
        sortString:
            searchParamsObject.sortString || getSortingBasedOnMarket({ market: countryCode }),
        offset,
        limit,
        priorityOperator: offset === 0 ? state?.pageData.brandConfig.priorityOperatorNid : null,
    };

    const newFacetParams = deepClone(newSearchParams);

    delete newFacetParams.limit; // This would be set as a default from product api

    return Promise.all([
        post(searchUrl, newSearchParams),
        post(facetsUrl, newFacetParams),
        post(facetsUrl, {}),
        [],
    ])
        .then(([search, facets, facetsAll]) => {
            const filterParamsList = makeFilterParamsList(
                searchParamsObject.filterParams,
                filterParamsSchema,
            );

            dispatch({
                type: actionTypes.newSearch,
                payload: {
                    data: search,
                    searchParams: newSearchParams,
                    filterParamsList,
                    filterParamsListActive: filterParamsList,
                    searchFacets: { ...facets },
                    queryString: queryString,
                    maxPrice: search.maxPrice,
                    maxDiscount: search.maxDiscount,
                    facetsAll,
                },
            });

            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
        })
        .catch((error) => {
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
            throw error;
        });
};

export const newFacetsSearch = (queryString: string) => (dispatch, getState) => {
    const state = getState();
    const isDesktopDevice = state.pageData.deviceType === 'desktop';
    const facetsUrlWithNum = `${state.pageData.appConfig.apiHost}/cruises/facets`;

    const searchParamsObject: ICruiseSearchApiObject = queryString
        ? qs.parse(queryString.replace('?', ''), { arrayLimit: 100 })
        : {};

    if (searchParamsObject?.filterParams?.inclFlight) {
        const { inclFlight } = searchParamsObject.filterParams;
        if (typeof inclFlight === 'string') {
            searchParamsObject.filterParams.inclFlight = isNaN(inclFlight)
                ? String(inclFlight).toLowerCase() === 'true'
                : Number(inclFlight) > 0;
        }
    }

    const offset =
        searchParamsObject.offset && isDesktopDevice
            ? parseInt((searchParamsObject.offset as any) as string, 10)
            : 0;
    const limit =
        searchParamsObject.limit && isDesktopDevice
            ? parseInt((searchParamsObject.limit as any) as string, 10)
            : 20;

    const priceMin =
        searchParamsObject?.filterParams?.priceMin || state?.pageData?.brandConfig?.priceMin || 0;

    dispatch({ type: actionTypes.searchLoadingToggle, payload: true });

    const { countryCode } = state?.pageData?.appConfig || (Markets.de as ICountryCode);

    const newSearchParams: ICruiseSearchApiObject = {
        ...defaultSearchParams,
        filterParams: { ...searchParamsObject.filterParams, priceMin },
        sortString:
            searchParamsObject.sortString || getSortingBasedOnMarket({ market: countryCode }),
        offset,
        limit,
        priorityOperator: offset === 0 ? state?.pageData.brandConfig.priorityOperatorNid : null,
    };

    const newFacetParams = deepClone(newSearchParams);

    delete newFacetParams.limit; // This would be set as a default from product api

    return Promise.resolve([post(facetsUrlWithNum, newFacetParams)])
        .then(([facetsAll]) => {
            dispatch({
                type: actionTypes.newFacetsSearch,
                payload: {
                    facetsAll,
                },
            });
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
        })
        .catch((error) => {
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
            throw error;
        });
};

export const loadMore = (page?: number) => (dispatch, getState) => {
    return new Promise((resolve: (value: boolean) => void) => {
        if (
            getState().search.searchResults.cruises.length <
            getState().search.searchResults.numResults
        ) {
            if (!isDesktop()) {
                dispatch({ type: actionTypes.appendNextResults });
            }
            dispatch({ type: actionTypes.searchLoadingToggle, payload: true });
            const history = getState().router.history;
            const url = `${getState().pageData.appConfig.apiHost}/cruises/search`;
            const searchParams = getState().search.searchParams;

            const newOffset = page ? page * 10 - 10 : searchParams.offset + searchParams.limit;
            searchParams.limit = 10;

            const newSearchParams = { ...searchParams, offset: newOffset };
            newSearchParams.filterParams = makeFilterParamsObject(
                getState().search.filterParamsListActive,
                filterParamsSchema,
            );
            if (isDesktop()) {
                dispatch({ type: actionTypes.showLoadingIndicator });

                const searchSlug = getState().pageData.brandConfig.slugsObj.searchresults;

                let currentLocation = getState().router.location;
                currentLocation.pathname = `/${searchSlug}`;

                const searchParams = { ...newSearchParams };
                const searchString = qs.stringify({ ...searchParams });
                const newLocation = { ...currentLocation, search: `?${searchString}` };

                history.push(newLocation);
                return resolve(false);
            }

            return post(url, newSearchParams)
                .then((response) => {
                    dispatch({
                        type: actionTypes.loadMore,
                        payload: {
                            data: response,
                            searchParams: newSearchParams,
                        },
                    });

                    dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
                    if (getState().search.appendAfterLoad) {
                        dispatch({ type: actionTypes.appendNextResults });
                    }
                    resolve(true);
                })
                .catch((_) => {
                    dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
                    resolve(false);
                }) as any;
        } else {
            resolve(false);
        }
    });
};

export const getRatesBulk = () => (dispatch, getState) => {
    const state = getState();
    const { router, pageData, requestForm } = state;

    const searchLivePricesFeatureFlag = pageData?.featureFlags?.find(
        (flag: IFeatureFlag) => flag.feature === 'searchLivePrices',
    );
    if (!searchLivePricesFeatureFlag?.isEnabled) {
        // Well, then it's not time for this feature
        return;
    }

    if (!router?.isBrowser) {
        // I see no reason to execute this request on the server,
        // especially since there will be excessive memory consumption.
        return;
    }

    const { searchResults, searchParams } = state.search;
    const cruises = searchResults?.cruises;
    const priorityCruise = searchResults?.priorityCruise;
    if (!(cruises?.length || priorityCruise)) {
        return;
    }

    const elementsNum = 10;
    const sortString = searchParams?.sortString;
    const lastCruises = cruises ? cruises.slice(-elementsNum) : [];
    const priorityCruises = priorityCruise ? [priorityCruise] : [];
    const combinedCruises = [...lastCruises, ...priorityCruises];
    const sails = combinedCruises.map(({ cruiseNid, cheapestSail, discountSail }) => ({
        cruiseId: cruiseNid,
        sailId: (sortString?.includes('discount') ? discountSail : cheapestSail)?.sailNid,
    }));

    const { apiHost, defaultCurrency, dualCurrency, countryCode } = pageData.appConfig;
    const { passengers, selectedPax } = requestForm;
    const { baby, child, junior, adult } = selectedPax;
    const selectedPaxForAPI = {
        num_baby: baby.num,
        num_child: child.num,
        num_junior: junior.num,
        num_adult: adult.num,
    };
    const inquiry: any = {
        market: countryCode,
        sails: sails.filter(({ cruiseId, sailId }) => cruiseId && sailId),
        currency: dualCurrency || defaultCurrency,
        passengers,
        selectedPax: selectedPaxForAPI,
    };

    const { promotionCode } = getQueryParams();
    if (promotionCode) {
        inquiry.promotionCode = promotionCode;
    }

    const payload = post(`${apiHost}/fareservice/bulkRatesBySail`, inquiry, true).catch((err) => {
        if (getState().router.isBrowser) {
            Sentry.captureException(err, { extra: inquiry });
            throw err;
        }
    });

    dispatch({ type: actionTypes.loadRatesBulk, payload });
};

export const toggleSearchFilter = () => (dispatch, getState) => {
    dispatch({
        type: actionTypes.openSearchFilter,
    });
};

export const loadTopSearchResults = () => (dispatch, getState) => {
    const url = `${getState().pageData.appConfig.apiHost}/cruises/search`;
    const searchParams: ICruiseSearchApiObject = {
        ...defaultSearchParams,
        filterParams: {
            banderoles: [331893],
        },
        limit: 3,
    };
    return post(url, searchParams).then((topResults) => {
        dispatch({ type: actionTypes.loadTopSearchResults, payload: topResults });
    });
};

export const loadSearchResults = (filterParams, limit) => (dispatch, getState) => {
    dispatch({ type: actionTypes.searchLoadingToggle, payload: true });
    const url = `${getState().pageData.appConfig.apiHost}/cruises/search`;
    const searchParams: ICruiseSearchApiObject = {
        ...defaultSearchParams,
        filterParams: filterParams,
        limit: limit,
    };
    return post(url, searchParams)
        .then((results) => {
            dispatch({ type: actionTypes.loadSearchResults, payload: results });
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
        })
        .catch((_) => {
            dispatch({ type: actionTypes.searchLoadingToggle, payload: false });
        });
};

export const setLoadingState = (isLoading: boolean) => (dispatch, getState) => {
    dispatch({
        type: isLoading ? actionTypes.showLoadingIndicator : actionTypes.hideLoadingIndicator,
        payload: isLoading,
    });
};

/**
 * Takes search parameters list from state, and does a new search by changing the URL...
 *
 * @param {ICruiseFilterParamValue[]} searchParamsList
 */
export const doNewSearchViaUrl = (searchParamsList: ICruiseFilterParamValue[]) => (
    dispatch,
    getState,
) => {
    const filterParamsList = getState().search.filterParamsList;
    const oldSearchApiObject = getState().search.searchParams;
    const history = getState().router.history;
    const filterParamsObject = makeFilterParamsObject(filterParamsList, filterParamsSchema);
    let currentLocation = getState().router.location;

    if (currentLocation.pathname === '') {
        const searchSlug = getState().pageData.brandConfig.slugsObj.searchresults;
        currentLocation.pathname = `/${searchSlug}`;
    }

    const searchApiObject = { ...oldSearchApiObject, filterParams: filterParamsObject };
    const { filterParams, sortString } = searchApiObject;

    const newSearchApiObject = {
        filterParams,
        sortString: sortString,
    };

    const searchString = qs.stringify(newSearchApiObject);
    const newLocation = { ...currentLocation, search: `?${searchString}` };

    if (newLocation.search !== history.location.search) {
        dispatch({ type: actionTypes.clearSearchResults, payload: null });
    }
    history.push(newLocation);
};
